import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/axios';
import StatsCard from '../Common/StatsCard';
import DataTable from '../Common/DataTable';
import DonutChart from '../Common/DonutChart';
import DashboardHero from '../Common/DashboardHero';
import { CURRENCY } from '../../config';

const ADMIN_STAFF_ROLES = ['principal', 'accountant', 'librarian', 'receptionist', 'lab_attendant'];

export default function SuperintendentDashboard() {
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [fees, setFees] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState('');
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get date 30 days ago to limit attendance data (fixes slow load time)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

      const [u, s, st, c, cl, a, sa, f] = await Promise.all([
        api.get('/users/').catch(() => ({ data: [] })),
        api.get('/students/').catch(() => ({ data: [] })),
        api.get('/staff/').catch(() => ({ data: [] })),
        api.get('/campuses/').catch(() => ({ data: [] })),
        api.get('/classes/').catch(() => ({ data: [] })),
        
        // ── OPTIMIZED: Only fetch last 30 days of attendance ──
        api.get(`/attendance/?date_from=${thirtyDaysAgo}`).catch(() => ({ data: [] })),
        
        api.get(`/staff-attendance/?date_from=${today}&date_to=${today}`).catch(() => ({ data: [] })),
        api.get('/fees/').catch(() => ({ data: [] })),
      ]);
      setUsers(u.data || []);
      setStudents(s.data || []);
      setStaff(st.data || []);
      setCampuses(c.data || []);
      setClasses(cl.data || []);
      setAttendance(a.data || []);
      setStaffAttendance(sa.data || []);
      setFees(f.data || []);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const allTeachers = useMemo(() => users.filter(u => u.role === 'teacher'), [users]);
  const allAdminStaff = useMemo(() => users.filter(u => ADMIN_STAFF_ROLES.includes(u.role)), [users]);

  const activeCampus = campuses.find(c => c.id === parseInt(selectedCampus));

  const filteredTeachers = selectedCampus ? allTeachers.filter(t => t.campus_name === activeCampus?.name) : allTeachers;
  const filteredAdminStaff = selectedCampus ? allAdminStaff.filter(u => u.campus_name === activeCampus?.name) : allAdminStaff;
  const filteredStaff = selectedCampus ? staff.filter(s => s.campus_id === activeCampus?.id) : staff;
  const filteredStudents = selectedCampus ? students.filter(s => s.campus_name === activeCampus?.name) : students;
  const filteredClasses = selectedCampus ? classes.filter(c => c.campus_id === activeCampus?.id) : classes;

  const filteredFees = useMemo(() => {
    if (!selectedCampus) return fees;
    const ids = new Set(filteredStudents.map(s => s.id));
    return fees.filter(f => ids.has(f.student_id));
  }, [fees, filteredStudents, selectedCampus]);

  const scopedStudentIds = useMemo(() => new Set(filteredStudents.map(s => s.id)), [filteredStudents]);
  const scopedAttendance = useMemo(() => attendance.filter(a => scopedStudentIds.has(a.student_id)), [attendance, scopedStudentIds]);
  const todayAttendance = useMemo(() => scopedAttendance.filter(a => a.date === today), [scopedAttendance, today]);
  const absentToday = useMemo(() => todayAttendance.filter(a => a.status === 'Absent'), [todayAttendance]);

  const teacherAttendanceToday = useMemo(() => {
    return filteredTeachers.map(t => {
      const rec = staffAttendance.find(r => r.person_type === 'teacher' && r.person_id === t.id);
      return {
        teacherId: t.id,
        name: `${t.first_name} ${t.last_name}`,
        campus: t.campus_name || '—',
        checkIn: rec?.check_in_local || '—',
        checkOut: rec?.check_out_local || '—',
        workedHours: rec?.worked_hours,
        status: rec?.status || 'Not Marked',
        source: rec?.source || '—',
      };
    });
  }, [filteredTeachers, staffAttendance]);

  const teachersPresentCount = teacherAttendanceToday.filter(t => t.status === 'Present' || t.status === 'Late').length;
  const teachersAbsentCount = teacherAttendanceToday.filter(t => t.status === 'Absent').length;
  const teachersNotMarkedCount = teacherAttendanceToday.filter(t => t.status === 'Not Marked').length;

  const staffAttendanceToday = useMemo(() => {
    return filteredStaff.map(s => {
      const rec = staffAttendance.find(r => r.person_type === 'staff' && r.person_id === s.id);
      return {
        staffId: s.id,
        name: `${s.first_name} ${s.last_name}`,
        designation: s.designation || '—',
        campus: campuses.find(c => c.id === s.campus_id)?.name || '—',
        checkIn: rec?.check_in_local || '—',
        checkOut: rec?.check_out_local || '—',
        workedHours: rec?.worked_hours,
        status: rec?.status || 'Not Marked',
      };
    });
  }, [filteredStaff, staffAttendance, campuses]);

  const staffPresentCount = staffAttendanceToday.filter(s => s.status === 'Present' || s.status === 'Late').length;
  const staffAbsentCount = staffAttendanceToday.filter(s => s.status === 'Absent').length;
  const staffNotMarkedCount = staffAttendanceToday.filter(s => s.status === 'Not Marked').length;

  const genderData = [
    { label: 'Male', value: filteredStudents.filter(s => s.gender === 'Male').length, color: '#3b82f6' },
    { label: 'Female', value: filteredStudents.filter(s => s.gender === 'Female').length, color: '#ec4899' },
    { label: 'Other', value: filteredStudents.filter(s => s.gender === 'Other').length, color: '#9ca3af' },
  ].filter(d => d.value > 0);

  const revenue = useMemo(() => {
    const totalBilled = filteredFees.reduce((s, f) => s + (f.amount || 0), 0);
    const totalPaid = filteredFees.reduce((s, f) => s + (f.paid_amount || 0), 0);
    const pending = totalBilled - totalPaid;
    const overdue = filteredFees.filter(f => f.status !== 'Paid' && f.due_date && new Date(f.due_date) < new Date()).length;
    return { totalBilled, totalPaid, pending, overdue };
  }, [filteredFees]);

  const teacherAttColumns = ['Name', 'Campus', 'Check-In', 'Check-Out', 'Hours', 'Status'];
  const teacherAttData = teacherAttendanceToday.map(t => ({
    Name: t.name,
    Campus: t.campus,
    'Check-In': <span className="font-mono text-xs">{t.checkIn}</span>,
    'Check-Out': <span className="font-mono text-xs">{t.checkOut}</span>,
    Hours: t.workedHours != null ? `${t.workedHours}h` : '—',
    Status: (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
        t.status === 'Present' ? 'bg-green-100 text-green-800'
        : t.status === 'Late' ? 'bg-yellow-100 text-yellow-800'
        : t.status === 'Absent' ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-600'
      }`}>{t.status}</span>
    ),
  }));

  const staffAttColumns = ['Name', 'Designation', 'Campus', 'Check-In', 'Check-Out', 'Hours', 'Status'];
  const staffAttData = staffAttendanceToday.map(s => ({
    Name: s.name,
    Designation: s.designation,
    Campus: s.campus,
    'Check-In': <span className="font-mono text-xs">{s.checkIn}</span>,
    'Check-Out': <span className="font-mono text-xs">{s.checkOut}</span>,
    Hours: s.workedHours != null ? `${s.workedHours}h` : '—',
    Status: (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
        s.status === 'Present' ? 'bg-green-100 text-green-800'
        : s.status === 'Late' ? 'bg-yellow-100 text-yellow-800'
        : s.status === 'Absent' ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-600'
      }`}>{s.status}</span>
    ),
  }));

  return (
    <div>
      <DashboardHero
        color="purple"
        icon="fa-user-tie"
        title="Superintendent Dashboard"
        subtitle={`${campuses.length} campuses · ${filteredStudents.length} students · ${filteredTeachers.length} teachers`}
      >
        <button
          onClick={fetchData}
          className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-sm font-medium transition"
        >
          <i className="fas fa-sync-alt mr-1"></i> Refresh
        </button>
      </DashboardHero>

      {/* Campus filter */}
      <div className="bg-white p-4 rounded shadow mb-4 flex items-center gap-4 flex-wrap">
        <label className="font-semibold text-sm">🏫 Filter by Campus:</label>
        <select
          className="p-2 border rounded"
          value={selectedCampus}
          onChange={(e) => setSelectedCampus(e.target.value)}
        >
          <option value="">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="ml-auto text-xs text-gray-500">
          <i className="fas fa-eye mr-1"></i> Cross-campus overview
        </span>
      </div>

      {/* Revenue */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-3">
          <i className="fas fa-dollar-sign text-green-600 mr-2"></i>
          Revenue Overview
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({selectedCampus ? activeCampus?.name : 'All campuses'})
          </span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
            <p className="text-xs text-blue-700 font-medium">Total Billed</p>
            <p className="text-xl font-bold text-blue-900">{CURRENCY}{revenue.totalBilled.toLocaleString()}</p>
          </div>
          <div className="bg-green-50 p-3 rounded border-l-4 border-green-500">
            <p className="text-xs text-green-700 font-medium">Collected</p>
            <p className="text-xl font-bold text-green-900">{CURRENCY}{revenue.totalPaid.toLocaleString()}</p>
          </div>
          <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-500">
            <p className="text-xs text-yellow-700 font-medium">Pending</p>
            <p className="text-xl font-bold text-yellow-900">{CURRENCY}{revenue.pending.toLocaleString()}</p>
          </div>
          <div className="bg-red-50 p-3 rounded border-l-4 border-red-500">
            <p className="text-xs text-red-700 font-medium">Overdue Invoices</p>
            <p className="text-xl font-bold text-red-900">{revenue.overdue}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatsCard label="Campuses" value={selectedCampus ? 1 : campuses.length} icon="fa-building" />
        <StatsCard label="Students" value={filteredStudents.length} icon="fa-user-graduate" />
        <StatsCard label="Teachers" value={filteredTeachers.length} icon="fa-chalkboard-teacher" />
        <StatsCard label="Absent Today" value={absentToday.length} icon="fa-user-times" />
        <StatsCard
          label="Teachers Present"
          value={`${teachersPresentCount}/${filteredTeachers.length}`}
          icon="fa-user-check"
        />
        <StatsCard
          label="Staff Present"
          value={`${staffPresentCount}/${filteredStaff.length}`}
          icon="fa-users-cog"
        />
      </div>

      {/* Teacher attendance */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold">
            <i className="fas fa-chalkboard-teacher text-blue-500 mr-2"></i>
            Teacher Attendance — Today
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredTeachers.length} total · {teachersPresentCount} present · {teachersAbsentCount} absent · {teachersNotMarkedCount} not marked)
            </span>
          </h2>
        </div>
        {teacherAttendanceToday.length === 0 ? (
          <p className="text-center py-6 text-gray-400 text-sm">No teachers found.</p>
        ) : (
          <DataTable columns={teacherAttColumns} data={teacherAttData} />
        )}
      </div>

      {/* Support staff */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold">
            <i className="fas fa-users-cog text-purple-500 mr-2"></i>
            Support Staff — Today
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredStaff.length} total · {staffPresentCount} present · {staffAbsentCount} absent · {staffNotMarkedCount} not marked)
            </span>
          </h2>
        </div>
        {filteredStaff.length === 0 ? (
          <p className="text-center py-6 text-gray-400 text-sm">No support staff registered.</p>
        ) : (
          <DataTable columns={staffAttColumns} data={staffAttData} />
        )}
      </div>

      {/* Gender distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <DonutChart title="👥 Gender Distribution" data={genderData} size={220} />
      </div>
    </div>
  );
}